/**
 * The `/api/v2/users/me` readiness gate, for the legacy app.
 *
 * `/api/v2/users/me` is the only endpoint that checks UMS/Hub onboarding state
 * and provisions the local PixelMe user. Everything else only looks the subject
 * up. The v1 `/user` this app loads its user from answers 401 when PixelMe has
 * no user yet, and 404 when the user exists but has no workspace. So this has
 * to run before `/user`, or a new or not-yet-onboarded user just looks signed
 * out.
 *
 * This app is also where Hub sends users back to after onboarding
 * (PIXELME_RETURN_TO_BASE_URL is the bare origin), so it can't leave the gate
 * to the v2 app.
 *
 * Mirrors `frontend/src/guards/user-data-guard.tsx` and
 * `frontend/src/utils/me-readiness.ts`:
 * - 200: the user exists locally and in UMS and has finished Hub onboarding (or
 *   is a legacy row with a null flag, as Hub treats it). `/me` creates the
 *   local user on the first such call.
 *   - `is_onboarded: false`: no organization/workspace yet (a user `/me` just
 *     created). Go to `/v2/onboard`, which creates it. This app has no
 *     workspace setup of its own and would hang on "Loading account".
 * - 409 HUB_REQUIRED with an allowlisted `hub_url`: go to Hub onboarding, once
 *   per tab, carrying the stored signup context.
 * - 503 READINESS_RETRYABLE: retry on the same 1/2/4s schedule.
 * - Any other readiness failure: hand over to `/v2/onboard`, whose guard shows
 *   the matching support or retry screen. Those screens stay in one place
 *   instead of being copied into this app.
 * - 401 and anything that isn't a readiness answer: reject, so callers keep
 *   their existing sign-in handling.
 */
import { apiCallNoErrorHandler } from './api';
import { computeReturnTo, isSafeReturnTo } from './returnTo';
import { consumeSignupAppContext } from './signupContext';
import { getHubBaseUrl } from '../auth/cognito';
import { MARKETING_PARAMS } from '../utils/marketingParams';

export const READINESS_RETRY_DELAYS_MS = [1000, 2000, 4000];

// Shared with the v2 app (same origin, same sessionStorage), so a redirect
// started in either app is not repeated by the other.
export const HUB_REDIRECT_COMMITTED_KEY = 'pixelme:hub_redirect_committed';

export const V2_ONBOARD_PATH = '/v2/onboard';

const HUB_ONBOARDING_ENTER_PATH = '/api/onboarding/enter';

const HUB_REQUIRED = 'HUB_REQUIRED';
const READINESS_RETRYABLE = 'READINESS_RETRYABLE';
const TERMINAL_READINESS_CODES = [
  HUB_REQUIRED,
  'IDENTITY_MIGRATION_REQUIRED',
  'USER_BLOCKED',
  'CONFIGURATION_ERROR',
  READINESS_RETRYABLE,
];

export const ReadinessResult = {
  /** Safe to load the user and render the app. */
  Ready: 'ready',
  /** A full-page navigation is under way; render nothing but a spinner. */
  Redirecting: 'redirecting',
  /**
   * A full-page navigation to Hub onboarding is under way - the usual answer
   * right after sign-up. Callers show the account-setup handoff message.
   */
  RedirectingToHub: 'redirecting_to_hub',
};

/** The message shown while the browser is on its way to Hub onboarding. */
export const HUB_REDIRECT_LOADING_TEXT = 'Your account is almost ready. Taking you to finish setting it up…';

export function isRedirectingResult(result) {
  return result === ReadinessResult.Redirecting || result === ReadinessResult.RedirectingToHub;
}

// Never gated: signing out must work for a user who is not onboarded, and
// `/sign-in` runs the gate itself with the return path from OIDC state.
const UNGATED_PATHS = ['/sign-out', '/sign-in'];

function normalizePathname(pathname) {
  return pathname.replace(/\/+$/, '') || '/';
}

export function isReadinessGatedPath(pathname = window.location.pathname) {
  const normalized = normalizePathname(pathname);

  return !UNGATED_PATHS.includes(normalized) && !normalized.startsWith('/invited/');
}

export function isAllowedHubEnterUrl(value) {
  if (typeof value !== 'string') {
    return false;
  }

  try {
    const target = new URL(value);
    const allowedBase = new URL(getHubBaseUrl());

    return target.origin === allowedBase.origin && normalizePathname(target.pathname) === HUB_ONBOARDING_ENTER_PATH;
  } catch {
    return false;
  }
}

export function buildHubEnterUrl(hubUrl, context) {
  const url = new URL(hubUrl);

  if (context && context.skipPermissionsFlow) {
    url.searchParams.set('skip_permissions_flow', 'true');
  }

  MARKETING_PARAMS.forEach((key) => {
    const value = context && context.marketingParams && context.marketingParams[key];

    if (value) {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

function hasSessionFlag(key) {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function setSessionFlag(key) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // Blocked storage only costs the loop guard; the redirect still happens.
  }
}

function clearSessionFlag(key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to clear.
  }
}

function readinessFailure(error) {
  const response = error && error.response;
  const data = (response && response.data) || {};
  const context = data.context && typeof data.context === 'object' ? data.context : {};

  return {
    status: response && response.status,
    code: typeof data.error_code === 'string' ? data.error_code.trim().toUpperCase() : '',
    hubUrl: context.hub_url,
  };
}

function defaultSleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function defaultNavigate(url) {
  window.location.assign(url);
}

/**
 * Runs the readiness gate for the signed-in user.
 *
 * @param {object} opts
 * @param {Storage} [opts.localStorage]
 * @param {Function} [opts.axios]
 * @param {string} [opts.returnTo] Where Hub should send the user back to.
 *   Defaults to the current location. Only same-origin relative paths are sent.
 * @returns {Promise<'ready'|'redirecting'|'redirecting_to_hub'>} Rejects with the API error when the
 *   answer is not a readiness outcome (401, network failure, unexpected status).
 */
export async function ensureUserReady({
  localStorage = window.localStorage,
  axios,
  returnTo = computeReturnTo(window.location.href, { excludePaths: UNGATED_PATHS }),
  sleep = defaultSleep,
  navigate = defaultNavigate,
} = {}) {
  // Built by hand rather than passed as `params`: apiCall snake-cases param
  // keys, and the API reads `returnTo`, not `return_to`.
  const url = isSafeReturnTo(returnTo) ? `/v2/users/me?returnTo=${encodeURIComponent(returnTo)}` : '/v2/users/me';

  for (let attempt = 0; ; attempt += 1) {
    let me;

    try {
      me = await apiCallNoErrorHandler({ localStorage, axios, method: 'get', url, secure: true });
    } catch (error) {
      const failure = readinessFailure(error);

      if (!TERMINAL_READINESS_CODES.includes(failure.code) || failure.status === 401) {
        throw error;
      }

      if (
        failure.status === 503 &&
        failure.code === READINESS_RETRYABLE &&
        attempt < READINESS_RETRY_DELAYS_MS.length
      ) {
        await sleep(READINESS_RETRY_DELAYS_MS[attempt]);
        continue;
      }

      if (
        failure.status === 409 &&
        failure.code === HUB_REQUIRED &&
        isAllowedHubEnterUrl(failure.hubUrl) &&
        !hasSessionFlag(HUB_REDIRECT_COMMITTED_KEY)
      ) {
        setSessionFlag(HUB_REDIRECT_COMMITTED_KEY);
        navigate(buildHubEnterUrl(failure.hubUrl, consumeSignupAppContext()));

        return ReadinessResult.RedirectingToHub;
      }

      // Blocked, identity conflict, misconfiguration, retries used up, or a
      // Hub redirect that already happened once in this tab and came back
      // still not ready. The v2 guard has the screen for each of these.
      navigate(V2_ONBOARD_PATH);

      return ReadinessResult.Redirecting;
    }

    clearSessionFlag(HUB_REDIRECT_COMMITTED_KEY);

    // Only an explicit false: if an older API left the field out, `/user`'s
    // 404 on the sign-in page still catches a user without a workspace.
    if (me && me.data && me.data.isOnboarded === false) {
      navigate(V2_ONBOARD_PATH);

      return ReadinessResult.Redirecting;
    }

    return ReadinessResult.Ready;
  }
}
